import seedrandom from 'seedrandom';

const PIPE_CONFIG = {
  WIDTH: 70,
  GAP_BASE: 180,
  MIN_GAP: 80,
  SPAWN_INTERVAL: 1800,
  BASE_SPEED: 3,
  SPEED_MULTIPLIER: 0.02,
  MAX_SPEED_MULTIPLIER: 3,
  GAP_REDUCTION_RATE: 1.5,
  MAX_GAP_REDUCTION: 100,
};

const COIN_TYPES = {
  BRONZE: {
    value: 1,
    color: '#cd7f32',
    radius: 8,
    spawnProbability: 0.6,
    minScore: 0,
  },
  SILVER: {
    value: 3,
    color: '#c0c0c0',
    radius: 10,
    spawnProbability: 0.3,
    minScore: 5,
  },
  GOLD: {
    value: 5,
    color: '#ffd700',
    radius: 12,
    spawnProbability: 0.1,
    minScore: 15,
  },
};

const COIN_CONFIG = {
  SPAWN_PROBABILITY: 0.7,
  COINS_PER_PIPE: { min: 1, max: 3 },
  HORIZONTAL_OFFSET: 35,
  MIN_DISTANCE_FROM_PIPE: 20,
};

export class DeterministicPipeGenerator {
  constructor(seed) {
    this.seed = String(seed ?? '0');
    this.pipeIndex = 0;
    this.lastGeneratedScore = 0;
  }

  getRandomForPipe(pipeNumber, currentScore = 0) {
    // Score-bucketed seed (changes every +5 score) per example.html
    return seedrandom(`${this.seed}-pipe-${pipeNumber}-score-${Math.floor(Number(currentScore || 0) / 5)}`);
  }

  determineCoinType(rng, currentScore) {
    const available = [];
    const probs = [];
    Object.values(COIN_TYPES).forEach((cfg) => {
      if (Number(currentScore || 0) >= cfg.minScore) {
        available.push(cfg);
        probs.push(cfg.spawnProbability);
      }
    });

    if (!available.length) return null;
    const total = probs.reduce((s, p) => s + p, 0);
    if (total <= 0) return available[0];

    let r = rng();
    for (let i = 0; i < available.length; i += 1) {
      const p = probs[i] / total;
      if (r < p) return available[i];
      r -= p;
    }
    return available[available.length - 1];
  }

  getCurrentGapSize(score = 0) {
    const s = Math.max(0, Number(score || 0));
    const reduction = Math.min(s * PIPE_CONFIG.GAP_REDUCTION_RATE, PIPE_CONFIG.MAX_GAP_REDUCTION);
    return Math.max(PIPE_CONFIG.GAP_BASE - reduction, PIPE_CONFIG.MIN_GAP);
  }

  generateCoinsForPipe({ pipeId, gapCenterY, gapSize, x }, rng, currentScore, height, groundHeight = 40) {
    const coins = [];
    if (rng() > COIN_CONFIG.SPAWN_PROBABILITY) return coins;

    const playableHeight = Math.max(0, height - groundHeight);
    const gapTop = gapCenterY - gapSize / 2;
    const gapBottom = gapCenterY + gapSize / 2;
    const availableHeight = gapBottom - gapTop;
    if (availableHeight < 50) return coins;

    const coinCount = Math.floor(
      COIN_CONFIG.COINS_PER_PIPE.min + rng() * (COIN_CONFIG.COINS_PER_PIPE.max - COIN_CONFIG.COINS_PER_PIPE.min + 1),
    );

    for (let i = 0; i < coinCount; i += 1) {
      const coinType = this.determineCoinType(rng, currentScore);
      if (!coinType) continue;

      const verticalRange = availableHeight - (2 * COIN_CONFIG.MIN_DISTANCE_FROM_PIPE + 2 * coinType.radius);
      if (verticalRange <= 0) continue;

      const y = gapTop
        + COIN_CONFIG.MIN_DISTANCE_FROM_PIPE
        + coinType.radius
        + rng() * verticalRange;

      // Keep coins within the playable area
      const clampedY = Math.max(coinType.radius, Math.min(playableHeight - coinType.radius, y));

      coins.push({
        id: `coin-${pipeId}-${i}`,
        dx: COIN_CONFIG.HORIZONTAL_OFFSET,
        y: clampedY,
        radius: coinType.radius,
        value: coinType.value,
        color: coinType.color,
        collected: false,
      });
    }

    return coins;
  }

  generatePipe({ index, score = 0, x, height, gapMargin = 90, groundHeight = 40 }) {
    const rng = this.getRandomForPipe(index, score);
    const playableHeight = Math.max(0, height - groundHeight);

    const difficulty = Math.min(1 + Number(score || 0) * PIPE_CONFIG.SPEED_MULTIPLIER, PIPE_CONFIG.MAX_SPEED_MULTIPLIER);
    const gapSize = this.getCurrentGapSize(score);

    const minY = gapMargin + gapSize / 2;
    const maxY = playableHeight - gapMargin - gapSize / 2;
    const gapCenterY = (maxY > minY) ? (minY + rng() * (maxY - minY)) : (playableHeight / 2);

    const pipeId = `pipe-${this.seed}-${index}`;
    const pipe = {
      id: pipeId,
      x,
      width: PIPE_CONFIG.WIDTH,
      gapY: gapCenterY,
      gapSize,
      speedMultiplier: difficulty,
      passed: false,
      seedIndex: index,
      generatedAtScore: score,
    };

    const coins = this.generateCoinsForPipe(
      { pipeId, gapCenterY, gapSize, x },
      rng,
      score,
      height,
      groundHeight,
    );

    this.lastGeneratedScore = score;
    return { ...pipe, coins };
  }

  generateInitialPipes({ score = 0, height, width, gapMargin = 90, groundHeight = 40, count = 10, startX = null, spacing = 260 }) {
    const pipes = [];
    const baseX = (startX == null) ? (width + 60) : startX;
    for (let i = 0; i < count; i += 1) {
      pipes.push(
        this.generatePipe({
          index: i,
          score,
          x: baseX + i * spacing,
          height,
          gapMargin,
          groundHeight,
        }),
      );
    }
    this.pipeIndex = count;
    return pipes;
  }

  generateNextPipe({ score = 0, x, height, gapMargin = 90, groundHeight = 40 }) {
    const pipe = this.generatePipe({ index: this.pipeIndex, score, x, height, gapMargin, groundHeight });
    this.pipeIndex += 1;
    return pipe;
  }

  reset() {
    this.pipeIndex = 0;
    this.lastGeneratedScore = 0;
  }
}

export function createPipeGenerator(seed) {
  return new DeterministicPipeGenerator(seed);
}

export default DeterministicPipeGenerator;
